import { Component, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import { ResearchGridDataService } from "src/app/research-grid-page/research-grid-data.service";
import { SetResearchBase } from "src/app/store/actions/research-grid-page.actions";
import { GetResearchDocsParams } from "src/app/store/models/research-grid-page.model";
import { Router, ActivatedRoute } from "@angular/router";
import { Observable, Subscription } from "rxjs";
import { AppService } from "src/app/app.service";
import {
  VAIconRegistryService,
  exclamationTriangle,
} from "@va/core-library/icons";

@Component({
  selector: "app-company-research",
  templateUrl: "./company-research.component.html",
  styleUrls: ["./company-research.component.scss"],
})
export class CompanyResearchComponent implements OnInit {
  isPrimayTicker = true;
  currentGridFilter: GetResearchDocsParams;
  showDedicatedPage: boolean;
  docs: Array<any> = [];
  hasMorePages = false;
  brokerMapping: any;
  researchDataReady = false;
  legacyUrl = `${window.caUrl}/app`;
  vaCompanyId: any;
  currentPage = 1;
  dataObservable: Observable<any>;
  errorMsg: string;
  hasError = false;
  viewEntitlements = false;
  loadingDocs = false;
  ticker: string;
  private routeSubscription: Subscription;
  private globalSettingsSubscription: Subscription;
  private documentCallSubscription: Subscription;
  companySymbologyId: any;
  showResearch = false;

  constructor(
    private dataService: ResearchGridDataService,
    private store: Store<AppState>,
    private router: Router,
    private route: ActivatedRoute,
    private appService: AppService,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([exclamationTriangle]);
  }

  ngOnInit() {
    this.routeSubscription = this.route.params.subscribe((param) => {
      const tckr = decodeURIComponent(param.ticker);
      if (tckr && tckr !== this.ticker) {
        this.ticker = tckr;
        this.dataObservable = this.dataService.getResearchBaseUrl(this.ticker);
      }
    });

    this.globalSettingsSubscription = this.store
      .select(stateGetter("globalHeader", "globalSettings"))
      .subscribe((data: any) => {
        if (data) {
          this.showDedicatedPage = data.showDedicatedPage;
          this.viewEntitlements = data.showEntitlementsPage;
        }
      });
  }

  getResearchGridData(jsonToPass) {
    const requestPayload = {
      ...jsonToPass,
      page: this.currentPage,
    };
    if (this.documentCallSubscription) {
      //Unsubscribe previously subscribed call and cancel ongoing calls
      this.documentCallSubscription.unsubscribe();
    }
    this.documentCallSubscription = this.dataService
      .getDocumentsCall(requestPayload)
      .subscribe(
        (respObject) => {
          {
            this.docs = this.docs.concat(respObject.docs);
            if (!respObject.docs) respObject.docs = [];
            if (respObject.docs.length === 0 && this.currentPage === 1) {
              this.hasError = true;
              if (this.viewEntitlements) {
                this.errorMsg =
                  "No research reports are available. Either no research has been published on this security from available providers or you are not entitled. Please request entitlement";
              } else {
                this.errorMsg =
                  "No research reports are available. Either no research has been published on this security from available providers or you are not entitled. Please contact support@visiblealpha.com to request entitlement.";
              }
            } else {
              if (respObject.docs.length === 0 || respObject.docs.length < 25) {
                this.hasMorePages = false;
              } else {
                this.hasMorePages = true;
              }
              for (let i = 0; i < this.docs.length; i++) {
                const row = this.docs[i];
                const providerObj = this.brokerMapping[row.publisher.id];
                if (providerObj) {
                  row.providerName = providerObj.bfullname;
                  row.providerLink = providerObj.entitled
                    ? `/reporting/broker/${providerObj.bn}`
                    : "";
                  row.fullProviderName = providerObj.bfullname;
                } else {
                  row.providerName = row.publisher.broker_shortname
                    ? row.publisher.broker_name
                    : "";
                  row.providerLink = "";
                  row.fullProviderName = row.publisher.broker_shortname
                    ? row.publisher.broker_name
                    : "";
                }
                const allAnalysts = [];
                let primaryAnalyst = "";
                for (let j = 0; j < row.analysts.length; j++) {
                  if (row.analysts[j].isPrimary && primaryAnalyst === "") {
                    primaryAnalyst = row.analysts[j].displayName;
                  }
                  allAnalysts.push(row.analysts[j].displayName);
                }
                row.primaryAnalyst =
                  primaryAnalyst !== ""
                    ? primaryAnalyst
                    : allAnalysts.length > 0
                      ? allAnalysts[0]
                      : "";
                if (allAnalysts.length) {
                  row.allAnalysts =
                    allAnalysts.length > 1
                      ? allAnalysts.join(", ")
                      : allAnalysts[0];
                }
                row.showInTab = true;
                //If date string is already stored, reuse it
                const datestring =
                  row.dataValue &&
                  row.dataValue.data &&
                  row.dataValue.data.publicationDate;
                row.dataValue = {
                  show: row.downloadable,
                  data: {
                    brokerShortName: row.publisher.broker_shortname,
                    companyId: this.vaCompanyId,
                    displayText: row.title,
                    id: row.id,
                    pageCount: row.page_count,
                    publicationDate: datestring
                      ? datestring
                      : row.publication_date,
                    ticker: this.ticker,
                    type: row.mime_type,
                    enableWatermark: row.publisher.enable_watermark,
                  },
                };
                row.publication_date = new Date(row.publication_date);
              }
              this.researchDataReady = true;
            }
            this.loadingDocs = false;
          }
        },
        (err) => {
          console.error(err);
          if (this.docs.length === 0) {
            this.hasError = true;
            if (this.viewEntitlements) {
              this.errorMsg =
                "No research reports are available. Either no research has been published on this security from available providers or you are not entitled. Please request entitlement";
            } else {
              this.errorMsg =
                "No research reports are available. Either no research has been published on this security from available providers or you are not entitled. Please contact support@visiblealpha.com to request entitlement.";
            }
          }
          this.loadingDocs = false;
        },
      );
  }

  viewDedicatedResearch(event) {
    this.appService.logUserAction(
      event,
      this.ticker,
      null,
      "Launch Research Viewer",
      "",
      "",
      null,
    );
    this.router.navigateByUrl(
      `/research/${this.ticker}/${this.companySymbologyId}`,
    );
  }

  redirectToView(event, rowData) {
    const pdfViewerLink = `/research_document/${rowData.id}/${this.ticker}`;
    window.open(pdfViewerLink, "_blank");
    const extras = {
      beginning_state_referrer: "Company_Page",
      research_id: rowData.id,
      research_type: rowData.type,
    };
    this.appService.logUserAction(
      event,
      this.ticker,
      "",
      "Research_Opened_FromTile",
      extras,
      "",
      "Research_View",
    );
  }

  onSuccess(response) {
    this.handleBaseUrlCall(response);
  }

  onFailure(response) {
    if (response.status === "error") {
      this.errorMsg = "Sorry, something went wrong.";
    }
  }

  handleBaseUrlCall(response) {
    this.vaCompanyId = response.data.company_id;
    this.companySymbologyId = response.data.symbologyCompanyId;

    this.showResearch =
      response.data.research_page_access &&
      response.data.symbologyCompanyId !== "" &&
      response.data.symbologyCompanyId !== null;

    const filters = {
      companyId: [this.companySymbologyId],
      primaryCo: this.isPrimayTicker ? 1 : 0,
      page: this.currentPage,
      pageSize: 25,
    };
    this.brokerMapping = response.oa_va_source_firm_mapping;

    const payload = {
      gridFilters: filters,
    };

    this.store.dispatch(
      new SetResearchBase(this.appService.deepClone(payload)),
    );

    this.currentGridFilter = filters;

    if (this.showResearch) {
      this.loadingDocs = true;
      this.getResearchGridData(filters);
    }
  }

  loadMoreItems() {
    if (this.hasMorePages) {
      this.currentPage++;
      this.getResearchGridData(this.currentGridFilter);
    }
  }

  ngOnDestroy() {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    if (this.globalSettingsSubscription) {
      this.globalSettingsSubscription.unsubscribe();
    }
    if (this.documentCallSubscription) {
      this.documentCallSubscription.unsubscribe();
    }
  }
}
